import React from 'react';
import { Form, Input, Button, Card, Tabs, Spin, ColorPicker, Upload, App, Row, Col, Image, Typography, Checkbox } from 'antd';
const { useWatch } = Form;
import { UploadOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/axios';

export const Settings: React.FC = () => {
    const [siteForm] = Form.useForm();
    const [notificationForm] = Form.useForm();
    const [themeForm] = Form.useForm();
    const queryClient = useQueryClient();
    const { message, modal } = App.useApp();

    const { data: settingsData, isLoading } = useQuery({
        queryKey: ['adminSettings'],
        queryFn: async () => {
            const response = await apiClient.get('/admin/settings');
            return response.data.data;
        }
    });

    const siteMutation = useMutation({
        mutationFn: async (values: any) => {
            const payload = Object.keys(values).map(key => ({
                settingKey: key,
                settingValue: values[key]
            }));
            await apiClient.put('/admin/settings/site', { settings: payload });
        },
        onSuccess: () => {
            message.success('Settings updated successfully.');
            queryClient.invalidateQueries({ queryKey: ['adminSettings'] });
        },
        onError: () => message.error('Failed to update settings.')
    });

    const themeMutation = useMutation({
        mutationFn: async (values: any) => {
            const payload = Object.keys(values).map(key => {
                // If it's a ColorPicker object, extract the hex string, otherwise it's a string
                const color = typeof values[key] === 'string' ? values[key] : values[key]?.toHexString();
                return {
                    variableName: key,
                    colorCode: color
                };
            });
            await apiClient.put('/admin/settings/theme', { themes: payload });
            // Reload page to reflect CSS changes globally if desired, or let contexts handle it.
            // Usually, the app will refetch public settings, but a hard reload is safer to verify injection.
            setTimeout(() => window.location.reload(), 1000);
        },
        onSuccess: () => {
            message.success('Theme settings updated successfully. Reloading...');
        },
        onError: () => message.error('Failed to update theme settings.')
    });
    
    const testNotificationMutation = useMutation({
        mutationFn: async (payload: { settings: any, channel: number, recipient: string }) => {
            await apiClient.post('/admin/settings/test-notification', {
                settings: payload.settings,
                channel: payload.channel,
                recipient: payload.recipient
            });
        },
        onSuccess: () => message.success('Test notification sent successfully.'),
        onError: () => message.error('Failed to send test notification.')
    });

    const handleTestNotification = async (channel: 'Email' | 'Telegram') => {
        const values = notificationForm.getFieldsValue();
        const notificationSettings = {
            smtpHost: values.SmtpHost,
            smtpPort: parseInt(values.SmtpPort) || 587,
            smtpUsername: values.SmtpUsername,
            smtpPassword: values.SmtpPassword,
            smtpEnableSsl: values.SmtpEnableSsl === 'true' || values.SmtpEnableSsl === true,
            telegramBotToken: values.TelegramBotToken,
            telegramChatId: values.TelegramChatId
        };

        const channelId = channel === 'Email' ? 0 : 1;
        const defaultRecipient = channel === 'Email' ? values.SmtpUsername : values.TelegramChatId;

        modal.confirm({
            title: `${channel} Testi`,
            content: (
                <div>
                    <p>Lütfen test bildiriminin gönderileceği adresi girin:</p>
                    <Input id="test-recipient" defaultValue={defaultRecipient} placeholder={channel === 'Email' ? 'Email adresi' : 'Chat ID'} />
                </div>
            ),
            onOk: async () => {
                const recipient = (document.getElementById('test-recipient') as HTMLInputElement).value;
                if (!recipient) {
                    message.error('Alıcı adresi boş olamaz.');
                    return;
                }
                testNotificationMutation.mutate({ settings: notificationSettings, channel: channelId, recipient });
            }
        });
    };

    const handleFileUpload = async (options: any, fieldKey: string) => {
        const { file, onSuccess, onError } = options;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('isPublic', 'true');

        try {
            const response = await apiClient.post('/admin/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const imgUrl = response.data.data.url;
            
            siteForm.setFieldValue(fieldKey, imgUrl);
            onSuccess('Ok');
            message.success(`${fieldKey} uploaded successfully`);
        } catch (error) {
            onError({ error });
            message.error(`Upload failed for ${fieldKey}`);
        }
    };

    const logoUrl = useWatch('LogoUrl', siteForm);
    const faviconUrl = useWatch('FaviconUrl', siteForm);

    if (isLoading) {
        return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
    }

    const siteTokensFallback = {
        SiteName: 'Mosaik Application',
        ContactEmail: 'info@example.com',
        DefaultLanguage: 'de',
        LogoUrl: '',
        FaviconUrl: '',
        RecaptchaSiteKey: '',
        RecaptchaSecretKey: '',
        ContactPhone: '',
        SmtpHost: '',
        SmtpPort: '587',
        SmtpUsername: '',
        SmtpPassword: '',
        SmtpEnableSsl: 'true',
        TelegramBotToken: '',
        TelegramChatId: '',
        NotificationEmails: '',
        ...(settingsData?.siteTokens || {})
    };

    const themeTokensFallback = settingsData?.themeTokens || {
        '--theme-primary': '#1890ff',
        '--theme-secondary': '#f0f2f5',
        '--theme-bg': '#ffffff',
        '--theme-text': '#333333'
    };

    const tabItems = [
        {
            key: 'site',
            label: 'Site Ayarları',
            children: (
                <Form form={siteForm} layout="vertical" initialValues={siteTokensFallback} onFinish={(values) => siteMutation.mutate(values)}>
                    <Row gutter={24}>
                        <Col span={24} md={12}>
                            <Form.Item name="SiteName" label="Site Adı">
                                <Input placeholder="Mosaik Offenbach" />
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12}>
                            <Form.Item name="ContactEmail" label="İletişim E-postası">
                                <Input placeholder="info@mosaik.de" />
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12}>
                            <Form.Item name="DefaultLanguage" label="Varsayılan Dil">
                                <Input placeholder="de" />
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12}>
                            <Form.Item name="ContactPhone" label="İletişim Telefonu">
                                <Input placeholder="+49..." />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Row gutter={16} align="bottom">
                                <Col flex="auto">
                                    <Form.Item name="LogoUrl" label="Logo URL">
                                        <Input placeholder="https://..." addonAfter={
                                            <Upload 
                                                customRequest={(options) => handleFileUpload(options, 'LogoUrl')} 
                                                showUploadList={false}
                                            >
                                                <Button type="text" size="small" icon={<UploadOutlined />}>Yükle</Button>
                                            </Upload>
                                        } />
                                    </Form.Item>
                                </Col>
                                {logoUrl && (
                                    <Col style={{ marginBottom: 24 }}>
                                        <Card size="small" bodyStyle={{ padding: 4 }} className="bg-gray-50 border-dashed">
                                            <Image src={logoUrl} alt="Logo Preview" height={60} style={{ objectFit: 'contain' }} />
                                        </Card>
                                    </Col>
                                )}
                            </Row>
                        </Col>

                        <Col span={24}>
                            <Row gutter={16} align="bottom">
                                <Col flex="auto">
                                    <Form.Item name="FaviconUrl" label="Favicon URL">
                                        <Input placeholder="https://..." addonAfter={
                                            <Upload 
                                                customRequest={(options) => handleFileUpload(options, 'FaviconUrl')} 
                                                showUploadList={false}
                                            >
                                                <Button type="text" size="small" icon={<UploadOutlined />}>Yükle</Button>
                                            </Upload>
                                        } />
                                    </Form.Item>
                                </Col>
                                {faviconUrl && (
                                    <Col style={{ marginBottom: 24 }}>
                                        <Card size="small" bodyStyle={{ padding: 4 }} className="bg-gray-50 border-dashed">
                                            <Image src={faviconUrl} alt="Favicon Preview" width={32} height={32} />
                                        </Card>
                                    </Col>
                                )}
                            </Row>
                        </Col>

                        <Col span={24} md={12}>
                            <Form.Item name="RecaptchaSiteKey" label="reCAPTCHA Site Key">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12}>
                            <Form.Item name="RecaptchaSecretKey" label="reCAPTCHA Secret Key">
                                <Input.Password />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item className="mt-4">
                                <Button type="primary" htmlType="submit" size="large" loading={siteMutation.isPending}>
                                    Site Ayarlarını Kaydet
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            )
        },
        {
            key: 'notifications',
            label: 'Bildirim Ayarları',
            children: (
                <Form 
                    form={notificationForm} 
                    layout="vertical" 
                    initialValues={siteTokensFallback} 
                    onFinish={(values) => {
                        const processedValues = { ...values };
                        // Convert boolean back to string for backend persistence
                        if (processedValues.SmtpEnableSsl !== undefined) {
                            processedValues.SmtpEnableSsl = processedValues.SmtpEnableSsl.toString();
                        }
                        siteMutation.mutate(processedValues);
                    }}
                >
                    <Row gutter={24}>
                        <Col span={24} className="flex justify-between items-center mb-4">
                            <Typography.Title level={4} style={{ margin: 0 }}>SMTP (E-posta) Ayarları</Typography.Title>
                            <Button 
                                size="small" 
                                style={{ backgroundColor: '#faad14', color: 'white', borderColor: '#faad14' }}
                                onClick={() => handleTestNotification('Email')} 
                                loading={testNotificationMutation.isPending}
                            >
                                Test Et
                            </Button>
                        </Col>
                        <Col span={24}>
                            <hr className="mb-6 opacity-20" />
                        </Col>

                        <Col span={24}>
                            <Form.Item name="NotificationEmails" label="Bildirim E-postaları (noktalı virgül ';' ile ayırın)">
                                <Input placeholder="admin@mosaik.de;info@mosaik.de" />
                            </Form.Item>
                        </Col>
                        
                        <Col span={24} md={16}>
                            <Form.Item name="SmtpHost" label="SMTP Sunucusu">
                                <Input placeholder="smtp.gmail.com" />
                            </Form.Item>
                        </Col>
                        <Col span={24} md={8}>
                            <Form.Item name="SmtpPort" label="Port">
                                <Input placeholder="587" />
                            </Form.Item>
                        </Col>

                        <Col span={24} md={12}>
                            <Form.Item name="SmtpUsername" label="SMTP Kullanıcı Adı">
                                <Input placeholder="user@gmail.com" />
                            </Form.Item>
                        </Col>
                        <Col span={24} md={12}>
                            <Form.Item name="SmtpPassword" label="SMTP Şifresi">
                                <Input.Password placeholder="••••••••" />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item 
                                name="SmtpEnableSsl" 
                                label="SSL/TLS Aktif" 
                                valuePropName="checked" 
                                getValueProps={(value) => ({ checked: value === 'true' || value === true })}
                            >
                                <Checkbox>Güvenli bağlantı (SSL/TLS) kullanılsın mı?</Checkbox>
                            </Form.Item>
                        </Col>

                        <Col span={24} className="mt-8 flex justify-between items-center mb-4">
                            <Typography.Title level={4} style={{ margin: 0 }}>Telegram Ayarları</Typography.Title>
                            <Button 
                                size="small" 
                                style={{ backgroundColor: '#faad14', color: 'white', borderColor: '#faad14' }}
                                onClick={() => handleTestNotification('Telegram')} 
                                loading={testNotificationMutation.isPending}
                            >
                                Test Et
                            </Button>
                        </Col>
                        <Col span={24}>
                            <hr className="mb-6 opacity-20" />
                        </Col>

                        <Col span={24} md={16}>
                            <Form.Item name="TelegramBotToken" label="Bot Token">
                                <Input.Password placeholder="123456789:ABCDefgh..." />
                            </Form.Item>
                        </Col>
                        <Col span={24} md={8}>
                            <Form.Item name="TelegramChatId" label="Varsayılan Chat ID">
                                <Input placeholder="-100..." />
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item className="mt-8">
                                <Button type="primary" htmlType="submit" size="large" loading={siteMutation.isPending}>
                                    Bildirim Ayarlarını Kaydet
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            )
        },
        {
            key: 'theme',
            label: 'Tema & Stil',
            children: (
                <Form form={themeForm} layout="vertical" initialValues={themeTokensFallback} onFinish={(values) => themeMutation.mutate(values)}>
                    <Row gutter={[24, 16]}>
                        {Object.keys(themeTokensFallback).map(key => (
                            <Col xs={24} sm={12} md={8} lg={6} key={key}>
                                <Form.Item name={key} label={key.replace('--theme-', '').replace('-', ' ').toUpperCase()}>
                                    <ColorPicker showText style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                        ))}
                    </Row>
                    <Form.Item className="mt-8">
                        <Button type="primary" htmlType="submit" size="large" loading={themeMutation.isPending}>
                            Tema Renklerini Kaydet
                        </Button>
                    </Form.Item>
                </Form>
            )
        }
    ];

    return (
        <div className="max-w-5xl mx-auto p-4">
            <Typography.Title level={2} className="mb-6">Global Yapılandırma</Typography.Title>
            <Card className="shadow-sm border-0 rounded-xl overflow-hidden">
                <Tabs 
                    items={tabItems} 
                    className="admin-settings-tabs" 
                    tabBarStyle={{ padding: '0 24px', marginBottom: 24, borderBottom: '1px solid #f0f0f0' }}
                />
            </Card>
        </div>
    );
};
